import React, { FunctionComponent, ReactNode, useCallback, useEffect, useState, useMemo } from 'react'
import {
  Text,
  Money as fMoney,
  DateComponent,
  Button,
  ColorTypes,
  useIsBreakpointLarge,
  Icon,
  Heading,
  useModalDeepLink,
  Link,
  useDate,
  Panel,
  Tag,
  Message,
} from '@afterpaytouch/core'
import { Table } from '../DataTable'
import styles from './style.module.scss'
import { useGetHasAnyOpenDispute, useUpcomingPaymentEffects } from './hooks'
import { hasAnyOpenDispute } from './utils'
import { Trans, useTranslation } from 'next-i18next'
import { LoadingListPage } from '../LoadingListPage'
import { PAGE_STATUSES, usePageStatus } from '../../hooks/usePageStatusTypes'
import { PayNowModal } from '../PayNowModal'
import { PaymentScheduleData, useConsumerHasAccountLevelPayments, useShouldPaymentSchedulePaymentSelectInstallment } from '../../state'
import { getInstallmentSequence, getTotalInstallmentCount } from '../../state/orders/utils'
import { useRouter } from 'next/router'
import { getMerchantName } from '@afterpaytouch/portal-api/consumer/ordertransactions'
import { useAmplitudeWithEnduringEventProperties } from '../../integrations/amplitude'
import { TrackingEvent } from '../../model/amplitude'
import { Money } from '../Money'
import { UpcomingRepaymentSchedule } from '../UpcomingRepaymentSchedule'
import { PaymentArrangementSchedule } from '@afterpaytouch/portal-api/consumer/hardship/types'
import { ButtonDisplay } from '../WelcomePanel/HeaderInfoDisplay/ButtonDisplay'
import { Money as IMoney, SupportedLocale } from '@afterpay/types'
import { NewUpcomingPaymentItem } from '../NewUpcomingPaymentItem'
import { StoreLogo } from '../StoreLogo'
import { useSearchParam } from '../../hooks/useSearchParam/useSearchParam'
import { stringToBoolean } from '@afterpay/utils/string'
import { Route } from '../../config/router'
import { useHardship, useFlag, LaunchDarklyFlags } from '../../hooks'
import { ExtendedPanel } from '../Panel'

const I18N_NAMESPACE = ['common', 'upcomingPayments', 'account', 'orders']
interface DataCellProps {
  bold?: boolean
  paymentSchedule: PaymentScheduleData
  color?: ColorTypes
}
interface UpcomingPaymentDataCellProps extends DataCellProps {
  lineClamp?: boolean
  testNameSpace?: string
  children: ReactNode
}

const UpcomingPaymentDataCell: FunctionComponent<UpcomingPaymentDataCellProps> = ({
  children,
  bold,
  paymentSchedule,
  color,
  lineClamp = false,
  testNameSpace = '',
}) => {
  const router = useRouter()
  const { logEvent } = useAmplitudeWithEnduringEventProperties()
  const { orderId, paymentType } = paymentSchedule
  return (
    <Table.LinkDataCell
      href={'orders/[orderId]'}
      renderAs={`orders/${orderId}`}
      onClick={() =>
        logEvent(TrackingEvent.CLICKED_ORDER_DETAIL, {
          orderId,
          outboundLink: router.pathname,
          paymentType,
        })
      }
    >
      <span className={styles.linkDataCell}>
        <Text bold={bold} size={'M'} color={color} lineClamp={lineClamp ? 1 : undefined} testNameSpace={`${testNameSpace}data-cell`}>
          {children}
        </Text>
      </span>
    </Table.LinkDataCell>
  )
}

export const UpcomingPayment: FunctionComponent = () => {
  const hasAccountLevelPayments = useConsumerHasAccountLevelPayments()
  const { data, isSuccess, isError, refetchUpcomingPayments } = useUpcomingPaymentEffects()
  // If account has payment plan status and no plan exists, the plan has been discharged (i.e. not paid in full).
  // If plan has been paid off <NoUpcomingPayment /> would be rendered
  const { setIsPayNowModalOpen, hardshipData, isHardshipAndComplete } = useHardship(hasAccountLevelPayments)
  const router = useRouter()
  const { t } = useTranslation(I18N_NAMESPACE)
  const isBpLargeUp = useIsBreakpointLarge()
  const [pageStatus, setPageStatus] = usePageStatus()
  const hasAnyDispute = useGetHasAnyOpenDispute(hasAccountLevelPayments)
  const isDisputeTagEnabled = useFlag(LaunchDarklyFlags.ConsumerProtectedDisputeEnabled)

  const payNowModalName = 'pay-now'
  // @ts-expect-error: OPERATION BLEED STOPPER
  const isPayNowFlowModalOpenParam = stringToBoolean(useSearchParam(`modal-${payNowModalName}`))
  const orderIdParam = useSearchParam('order-id')
  const currentScheduleParam = useMemo(
    () => data?.upcomingPaymentScheduleData.find((paymentSchedule) => paymentSchedule.orderId === orderIdParam),
    [data, orderIdParam]
  )
  // @ts-expect-error: OPERATION BLEED STOPPER
  const [currentSchedule, setCurrentSchedule] = useState<PaymentScheduleData | PaymentArrangementSchedule>(currentScheduleParam)

  const [isModalOpen, setIsModalOpen] = useState(isPayNowFlowModalOpenParam)
  useModalDeepLink(isModalOpen, payNowModalName)

  const [isOverdue, setIsOverdue] = useState<boolean>(false)
  const closePaymentModal = (): void => {
    setIsModalOpen(false)
    // @ts-expect-error: OPERATION BLEED STOPPER
    setCurrentSchedule(null)
    setIsOverdue(false)
  }

  const currentScheduleData = Boolean(currentSchedule) && 'paymentScheduleId' in currentSchedule ? currentSchedule : null
  const shouldPaymentSchedulePaymentSelectInstallment = useShouldPaymentSchedulePaymentSelectInstallment(
    currentScheduleData?.paymentType,
    currentScheduleData?.isInstallmentLastAndOwed
  )
  const { logEvent } = useAmplitudeWithEnduringEventProperties()
  const locale = router?.locale as SupportedLocale
  const { formatDate } = useDate({ locale, year: 'numeric', month: 'short', weekday: 'short' })

  const tableCellHeaders = [
    t('upcomingPayments:activeUpcomingPayments:merchant'),
    t('upcomingPayments:activeUpcomingPayments:paymentNo'),
    t('upcomingPayments:activeUpcomingPayments:dueDate'),
    t('upcomingPayments:activeUpcomingPayments:dueIn'),
    <div className='flex flex-row items-center' key='amount-due'>
      <Text bold size={'S'} color={'Gray40'}>
        {t('upcomingPayments:activeUpcomingPayments:amountDue')}
      </Text>
      <ButtonDisplay isOwedAmount={true} isUpcomingPaymentsLinkRequired={false} />
    </div>,
  ]

  const hardshipTableCellHeaders = [
    t('upcomingPayments:repaymentSchedule:repaymentPlan'),
    t('upcomingPayments:activeUpcomingPayments:dueDate'),
    t('upcomingPayments:activeUpcomingPayments:dueIn'),
    t('upcomingPayments:activeUpcomingPayments:amountDue'),
  ]

  const hardshipHeading = (): JSX.Element => {
    return (
      <>
        <div className='flex w-full'>
          <div className='mr-3'>
            <Icon name='WarningAlt' color='Fire' size='Small' />
          </div>
          <Text color='Fire' size={isBpLargeUp ? 'M' : 'S'} testNameSpace='hardship-heading'>
            <Trans
              i18nKey={'orders:orderList:hardship:header'}
              components={{ supportLink: <Link href={t('orders:orderList:hardship:supportLinkHref')} target='_blank' kind='Underline' /> }}
            />
          </Text>
        </div>
        <div className='mx-0 my-4 h-px bg-[#f0f0f0]'></div>
      </>
    )
  }

  useEffect(() => {
    if (isSuccess) {
      setPageStatus(PAGE_STATUSES.SUCCESS)
    }
    if (isError) {
      setPageStatus(PAGE_STATUSES.ERROR)
    }
  }, [isSuccess, isError])

  useEffect(() => {
    if (currentSchedule != null) {
      setIsModalOpen(true)
    }
  }, [currentSchedule, setIsModalOpen])

  useEffect(() => {
    setIsPayNowModalOpen(isModalOpen)
  }, [isModalOpen])

  const renderDesktopHeaderInfo = (dueDays: number, amount: IMoney): JSX.Element => {
    return (
      <div className={styles.headInfo} key={dueDays} data-testid='upcoming-payment-desktop-header'>
        <>
          <Money value={amount} size='XXXL' bold={true} />
          <Text>
            <Trans values={{ dueDays }} i18nKey='upcomingPayments:activeUpcomingPayments:dueBy' />
          </Text>
        </>
      </div>
    )
  }

  const renderMobileHeaderInfo = (dueDays: number, amount: IMoney): JSX.Element => {
    return (
      <div className={styles.headInfoMobile} key={dueDays}>
        <Money value={amount} size='L' bold={true} />
        <Text size='XS'>
          <Trans values={{ dueDays }} i18nKey='upcomingPayments:activeUpcomingPayments:dueByMobile' />
        </Text>
      </div>
    )
  }

  const handleClick = useCallback(
    (paymentSchedule: PaymentScheduleData | PaymentArrangementSchedule, dueInDays: string): void => {
      setCurrentSchedule(paymentSchedule)
      setIsOverdue(isOverdue)
      const isPaymentArrangement = 'paymentPlanUuid' in paymentSchedule
      logEvent(TrackingEvent.CLICKED_PAY_NOW, {
        installmentDueDate: isPaymentArrangement ? paymentSchedule?.overdueDatetime : paymentSchedule.installmentDueDate,
        outboundLink: router.pathname,
        installmentStatus: dueInDays,
      })
    },
    [currentSchedule, isOverdue, logEvent]
  )

  const openPayNowModal = (): JSX.Element | null => {
    const orderProps = {
      // @TODO: Should be a typeguard
      order: {
        merchant: getMerchantName(currentScheduleData?.merchant),
        paymentType: currentScheduleData?.paymentType,
        orderId: currentScheduleData?.orderId,
      },
    }
    const paymentProps = {
      // @TODO: Should be a typeguard
      payment: {
        ids: [{ id: currentScheduleData?.paymentScheduleId, sequence: currentScheduleData?.installmentSequence }],
        total: currentScheduleData?.amountDue,
      },
      isOverdue,
    }

    if (currentScheduleData?.clearpayPartiallyMigrated) {
      return (
        // @ts-expect-error: OPERATION BLEED STOPPER
        <PayNowModal order={orderProps?.order} isModalOpen={isModalOpen} closeModal={closePaymentModal} initalStep={'ClearpayPartiallyMigratedRedirection'} />
      )
    }

    return isModalOpen && currentSchedule ? (
      <PayNowModal
        isModalOpen={isModalOpen}
        closeModal={closePaymentModal}
        initalStep={shouldPaymentSchedulePaymentSelectInstallment ? 'Installments' : 'Payment'}
        // @ts-expect-error: OPERATION BLEED STOPPER
        payment={paymentProps?.payment}
        // @ts-expect-error: OPERATION BLEED STOPPER
        order={orderProps?.order}
        achFlowEntryDeepLink={`${Route.UPCOMING_PAYMENTS__PAYNOW_ADDBANKACCOUNTMODAL}&order-id=${orderProps.order.orderId}`}
      />
    ) : null
  }
  const renderDesktopList = (): JSX.Element => {
    const headings = hasAccountLevelPayments && !isHardshipAndComplete ? hardshipTableCellHeaders : tableCellHeaders
    return (
      <>
        {isDisputeTagEnabled && hasAnyDispute && (
          <div className='-ml-4 -mr-4 mb-4'>
            <Message
              kind={'Basic'}
              description={
                <>
                  <Text>{t('upcomingPayments:disputes:info')}</Text>
                </>
              }
              iconName='Info'
              testNameSpace='regz-dispute-banner'
            />
          </div>
        )}
        {isHardshipAndComplete && hardshipHeading()}
        <Table>
          <Table.Head>
            <Table.Row testNameSpace='upcoming-payment'>
              {headings.map((tableHeading, i) => (
                <Table.HeaderCell key={`table-row-headercell-${i}`} scope={'col'}>
                  {typeof tableHeading === 'string' ? (
                    <Text bold size={'S'} color={'Gray40'}>
                      {tableHeading}
                    </Text>
                  ) : (
                    tableHeading
                  )}
                </Table.HeaderCell>
              ))}
            </Table.Row>
          </Table.Head>
          <Table.Content>
            {hasAccountLevelPayments && hardshipData != null ? (
              <UpcomingRepaymentSchedule repaymentSchedule={hardshipData} locale={locale} isDesktopSize={isBpLargeUp} onClick={handleClick} />
            ) : (
              isSuccess &&
              data.upcomingPaymentScheduleData.map((paymentSchedule) => {
                const dueInDays = t(paymentSchedule.formatDueInDays)
                const isOverDue = dueInDays === t('upcomingPayments:activeUpcomingPayments:overDue')
                const {
                  orderId,
                  paymentScheduleId,
                  merchant,
                  installmentDueDate,
                  amountDue,
                  isAllowedToBePaid,
                  isPaymentPending,
                  paymentType,
                  paymentUpFront,
                  openRegZDispute,
                  hasOpenDispute,
                  hasStandardDisputeOpen,
                } = paymentSchedule
                const installmentSequence = getInstallmentSequence(paymentSchedule.installmentSequence, paymentUpFront, paymentType)
                const installmentsCount = getTotalInstallmentCount(paymentSchedule.installmentsCount, paymentUpFront, paymentType)

                return (
                  <React.Fragment key={paymentScheduleId ?? orderId}>
                    <Table.Row>
                      <Table.LinkDataCell
                        href={'orders/[orderId]'}
                        renderAs={`orders/${orderId}`}
                        onClick={() =>
                          logEvent(TrackingEvent.CLICKED_ORDER_DETAIL, {
                            orderId,
                            outboundLink: router.pathname,
                            paymentType,
                          })
                        }
                      >
                        <div className='flex items-center gap-x-4'>
                          <StoreLogo merchant={merchant} />
                          <span className='flex-1'>
                            <Heading lineClamp={1} testNameSpace={`merchant-data-cell`}>
                              {getMerchantName(merchant)}
                            </Heading>
                          </span>
                        </div>
                      </Table.LinkDataCell>
                      <UpcomingPaymentDataCell paymentSchedule={paymentSchedule}>
                        <div className={'my-auto'}>
                          <Text>{`${installmentSequence} of ${installmentsCount}`}</Text>
                        </div>
                      </UpcomingPaymentDataCell>
                      <UpcomingPaymentDataCell paymentSchedule={paymentSchedule}>
                        <DateComponent date={installmentDueDate} locale={locale} year='numeric' month='short' weekday='short' />
                      </UpcomingPaymentDataCell>
                      {hasAccountLevelPayments ? (
                        <UpcomingPaymentDataCell paymentSchedule={paymentSchedule}>{t('common:accountStatus:onHold')}</UpcomingPaymentDataCell>
                      ) : (
                        <UpcomingPaymentDataCell
                          paymentSchedule={paymentSchedule}
                          bold={isOverDue}
                          color={isOverDue ? 'Fire' : undefined}
                          testNameSpace='dueIn-'
                        >
                          {isDisputeTagEnabled && hasAnyOpenDispute({ openRegZDispute, hasOpenDispute, hasStandardDisputeOpen }) ? (
                            <span className='whitespace-nowrap'>
                              <Tag kind='Black'>{t('common:inDisputeTag')}</Tag>
                            </span>
                          ) : (
                            <Trans values={{ count: paymentSchedule.dueInDays }} i18nKey={dueInDays} />
                          )}
                        </UpcomingPaymentDataCell>
                      )}
                      <UpcomingPaymentDataCell paymentSchedule={paymentSchedule}>
                        <Money value={amountDue} size='M' />
                      </UpcomingPaymentDataCell>
                      <Table.DataCell>
                        {isAllowedToBePaid ? (
                          <div className={styles.payButton}>
                            <Button
                              size='Regular'
                              testNameSpace='pay-now'
                              onClick={() => handleClick(paymentSchedule, dueInDays)}
                              disabled={isHardshipAndComplete}
                            >
                              {t('upcomingPayments:activeUpcomingPayments:payNow')}
                            </Button>
                          </div>
                        ) : (
                          isPaymentPending && (
                            <Text bold size={'S'} color={'Gray40'} testNameSpace='upcoming-payment-payment-pending'>
                              {t('upcomingPayments:activeUpcomingPayments:paymentPending')}
                            </Text>
                          )
                        )}
                      </Table.DataCell>
                      <UpcomingPaymentDataCell paymentSchedule={paymentSchedule}>
                        <Icon.CaretRight size='ExtraSmall' color='Gray60' />
                      </UpcomingPaymentDataCell>
                    </Table.Row>
                  </React.Fragment>
                )
              })
            )}
          </Table.Content>
        </Table>
        {openPayNowModal()}
      </>
    )
  }

  const renderCta = useCallback(
    (paymentSchedule): ReactNode => {
      const dueInDays = t(paymentSchedule.formatDueInDays)
      return (
        <div className='flex h-10'>
          {paymentSchedule.isAllowedToBePaid ? (
            <Button size='Small' padding='Fixed' onClick={() => handleClick(paymentSchedule, dueInDays)} disabled={isHardshipAndComplete}>
              {t('upcomingPayments:activeUpcomingPayments:payButtonMobile', {
                money: fMoney({ ...paymentSchedule.amountDue, locale }),
              })}
            </Button>
          ) : (
            <div className='pr-2'>
              <Money value={paymentSchedule.amountDue} size='S' />
            </div>
          )}
        </div>
      )
    },
    [currentSchedule, t, data?.upcomingPaymentScheduleData]
  )

  const renderMobileList = (): JSX.Element => {
    return (
      <>
        {isDisputeTagEnabled && hasAnyDispute && hasAccountLevelPayments && (
          <div className='mb-4'>
            <Message
              kind={'Basic'}
              description={
                <>
                  <Text bold>{t('upcomingPayments:disputes:info')}</Text>
                  <Text>{t('upcomingPayments:disputes:info2')}</Text>
                </>
              }
              iconName='Info'
              testNameSpace='regz-dispute-banner'
            />
          </div>
        )}
        {isHardshipAndComplete && hardshipHeading()}
        {hasAccountLevelPayments && hardshipData != null ? (
          <UpcomingRepaymentSchedule repaymentSchedule={hardshipData} locale={locale} isDesktopSize={isBpLargeUp} onClick={handleClick} />
        ) : (
          isSuccess &&
          data.upcomingPaymentScheduleData.map((paymentSchedule) => {
            const dueInDays = t(paymentSchedule.formatDueInDays)
            const isOverDue = dueInDays === t('upcomingPayments:activeUpcomingPayments:overDue')
            const dueToday = dueInDays === t('upcomingPayments:activeUpcomingPayments:dueNow')
            const dueStatus = isOverDue || dueToday ? dueInDays : formatDate(paymentSchedule.installmentDueDate)
            return (
              <NewUpcomingPaymentItem
                key={`upcomingPaymentItem-${paymentSchedule.paymentScheduleId ?? paymentSchedule.orderId}`}
                orderId={paymentSchedule.orderId}
                paymentType={paymentSchedule.paymentType}
                dateDue={dueStatus}
                merchant={paymentSchedule.merchant}
                installmentSequence={paymentSchedule.installmentSequence}
                installmentsCount={paymentSchedule.installmentsCount}
                isOverdue={isOverDue}
                action={() => renderCta(paymentSchedule)}
                hasOpenRegzDispute={paymentSchedule.openRegZDispute}
                hasOpenDispute={paymentSchedule.hasOpenDispute}
                hasStandardDisputeOpen={paymentSchedule.hasStandardDisputeOpen}
              />
            )
          })
        )}
        {openPayNowModal()}
      </>
    )
  }

  const renderActiveUpcomingPayments = (): React.ReactElement => (
    <Panel>
      <Panel.Head>
        <div className={styles.upcomingPaymentHeader}>
          {data.amountDueData?.paymentPeriods.map((paymentPeriod) => {
            if (isBpLargeUp) {
              return renderDesktopHeaderInfo(paymentPeriod.period, paymentPeriod.total)
            } else {
              return renderMobileHeaderInfo(paymentPeriod.period, paymentPeriod.total)
            }
          })}
        </div>
      </Panel.Head>
      <Panel.Content bordered={true}>{isBpLargeUp ? renderDesktopList() : renderMobileList()}</Panel.Content>
    </Panel>
  )

  switch (pageStatus) {
    case PAGE_STATUSES.SUCCESS:
      return renderActiveUpcomingPayments()
    case PAGE_STATUSES.ERROR:
      return (
        <Panel>
          <Panel.Content>
            <ExtendedPanel.Error onClick={refetchUpcomingPayments} testNameSpace='upcoming-payment' />
          </Panel.Content>
        </Panel>
      )
    case PAGE_STATUSES.LOADING:
      return <LoadingListPage tableCellHeaders={tableCellHeaders} testNameSpace='upcoming-payment' />
  }
}
